import React, {useEffect,useMemo,useState} from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import FileUploadSports from './FileUploadSports'
import './settings.css'

/* ------------------------------------------------------------------
   Icons — stroke: currentColor so they inherit the token colour.
   ------------------------------------------------------------------ */
const IconEdit=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 20h9"/>
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
  </svg>
)

const IconX=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>
)

const IconSearch=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7"/>
    <path d="m20 20-3.5-3.5"/>
  </svg>
)

/* ------------------------------------------------------------------
   THEME TOKENS
   react-data-table-component styles through a JS object, so the table
   cannot read CSS variables on its own. We read them off :root and
   re-read whenever the theme attribute or the OS preference changes,
   which keeps the table in step with the rest of the product.
   ------------------------------------------------------------------ */
const TOKEN_NAMES=[
  '--panel','--panel-raise','--line','--line-soft',
  '--ink','--ink-2','--ink-3','--accent','--accent-wash',
  '--mono','--sans','--step-0'
]

const readTokens=()=>{
  const out={}
  if (typeof window==='undefined'||!document.documentElement) return out
  const styles=window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach(name=>{
    out[name]=String(styles.getPropertyValue(name)||'').trim()
  })
  return out
}

function useThemeTokens(){
  const [tokens,setTokens]=useState(readTokens)

  useEffect(()=>{
    let frame=null
    const refresh=()=>{
      if (frame) window.cancelAnimationFrame(frame)
      /* read after the new custom properties have landed */
      frame=window.requestAnimationFrame(()=>setTokens(readTokens()))
    }

    const observer=new MutationObserver(refresh)
    observer.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme','class','style']})

    let media=null
    if (window.matchMedia){
      media=window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change',refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return ()=>{
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media){
        if (media.removeEventListener) media.removeEventListener('change',refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  },[])

  return tokens
}

function buildTableStyles(t){
  return {
    table:{ style:{ backgroundColor:t['--panel'], color:t['--ink'] } },
    tableWrapper:{ style:{ display:'block' } },
    head:{ style:{ color:t['--ink-3'] } },
    headRow:{
      style:{
        backgroundColor:t['--panel-raise'],
        color:t['--ink-3'],
        minHeight:'42px',
        borderBottomWidth:'1px',
        borderBottomColor:t['--line'],
        borderBottomStyle:'solid'
      }
    },
    headCells:{
      style:{
        paddingLeft:'12px',
        paddingRight:'12px',
        fontFamily:t['--mono'],
        fontSize:'10px',
        fontWeight:600,
        letterSpacing:'0.08em',
        textTransform:'uppercase',
        color:t['--ink-3']
      }
    },
    rows:{
      style:{
        backgroundColor:t['--panel'],
        color:t['--ink'],
        fontFamily:t['--sans'],
        fontSize:t['--step-0']||'0.875rem',
        minHeight:'50px',
        '&:not(:last-of-type)':{
          borderBottomWidth:'1px',
          borderBottomStyle:'solid',
          borderBottomColor:t['--line-soft']
        }
      },
      highlightOnHoverStyle:{
        backgroundColor:t['--accent-wash'],
        color:t['--ink'],
        borderBottomColor:t['--line-soft'],
        outlineWidth:'0px',
        outlineColor:t['--panel'],
        transitionDuration:'0.15s',
        transitionProperty:'background-color'
      }
    },
    cells:{ style:{ paddingLeft:'12px', paddingRight:'12px' } },
    pagination:{
      style:{
        backgroundColor:t['--panel'],
        color:t['--ink-3'],
        fontSize:'12px',
        minHeight:'50px',
        borderTopWidth:'1px',
        borderTopStyle:'solid',
        borderTopColor:t['--line']
      },
      pageButtonsStyle:{
        color:t['--ink-2'],
        fill:t['--ink-2'],
        backgroundColor:'transparent',
        '&:disabled':{ color:t['--ink-3'], fill:t['--ink-3'] },
        '&:hover:not(:disabled)':{ backgroundColor:t['--panel-raise'] },
        '&:focus':{ outline:'none', backgroundColor:t['--accent-wash'] }
      }
    },
    noData:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } },
    progress:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } }
  }
}

export default function Sports() {
    const [data,setData]=useState([])
    /* true from the first paint: loadData() fires on mount, so an empty-state
       flash before the request starts would be a lie */
    const [isLoading,setIsLoading]=useState(true)
    const [failed,setFailed]=useState(false)
    const [show, setShow] = useState(false)
    const [record,setRecord]=useState({})
    const [q,setQ]=useState('')

    /* inline feedback — this is what replaced the alert() calls */
    const [note,setNote]=useState(null)
    const [modalNote,setModalNote]=useState(null)
    const [askDelete,setAskDelete]=useState(false)

    const tokens=useThemeTokens()

    const columns = [
        {
            name: '#',
            selector: (row,index) =>  index+1,
            sortable: false,
            width: '58px',
            center: true
        },
        {
            name: '',
            cell: row => (
              <button
                type="button"
                className="st-rowbtn"
                onClick={()=>showModal(row.SportID)}
                aria-label={'Edit — '+(row.SportName||'')}
                title="Edit"
              >
                <IconEdit/>
              </button>
            ),
            width: '62px',
            center: true,
            ignoreRowClick: true
        },
        {
            name: 'Name',
            selector: row =>  row.SportName,
            cell: row => <span className="st-cell-name">{row.SportName}</span>,
            sortable: true,
            minWidth: '180px'
        },
        {
            name: 'Stat Categories',
            selector: row =>  row.Tags,
            cell: row => (
              row.Tags
                ? <span className="st-cell-mono">{row.Tags}</span>
                : <span className="st-cell-dim">—</span>
            ),
            sortable: true,
            minWidth: '260px',
            wrap: true
        }
    ]

    const handleClose = () => {
      setShow(false)
      setModalNote(null)
      setAskDelete(false)
    }

    useEffect(()=>{
        loadData()
    },[])

    /* Escape closes the dialog. */
    useEffect(()=>{
      if (!show) return undefined
      const onKey=(ev)=>{ if (ev.key==='Escape') handleClose() }
      document.addEventListener('keydown',onKey)
      return ()=>document.removeEventListener('keydown',onKey)
    },[show])

      const loadData=()=>{
        setIsLoading(true)
        setFailed(false)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/sports',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
          setData(res.data.results)
          setIsLoading(false)

        }).catch(err=>{
            setIsLoading(false)
            setFailed(true)
            setNote({tone:'crit',text:'The sports list could not be loaded. Check your connection and try again.'})
            console.log(err)
        })
      }

      const showModal=(id)=>{
        setModalNote(null)
        setAskDelete(false)
        if (id==0){
            setRecord({ModalTitle:'NEW RECORD', id:0,Title:'',Tags:''})
            setShow(true)
        }else{
            const recObj=data.filter(item=>item.SportID==id)[0]

            setRecord({ModalTitle:recObj.SportName, id:recObj.SportID,Title:recObj.SportName,Tags:recObj.Tags})
            setShow(true)
        }

      }

      const save=(e)=>{
        e.preventDefault()
        setModalNote(null)
        axios.post(process.env.REACT_APP_API_SERVER+'admin/sports',record,{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                }
            })
          .then(res=>{
            if (res.data.err==0){
                loadData()
            }
            setShow(false)
            setAskDelete(false)
            setNote({tone:res.data.err==0 ? 'good' : 'crit',text:res.data.msg})
        }).catch(err=>{
            console.log(err)
            setModalNote({tone:'crit',text:'Saving failed. Please try again.'})
        })
      }

      const deleteRecord=()=>{
            axios.delete(process.env.REACT_APP_API_SERVER+'admin/sports/'+record.id,{
                headers: {
                    'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                    }
                })
              .then(res=>{
                if (res.data.err==0){
                    loadData()
                }
                setShow(false)
                setAskDelete(false)
                setNote({tone:res.data.err==0 ? 'good' : 'crit',text:res.data.msg})
            }).catch(err=>{
                console.log(err)
                setAskDelete(false)
                setModalNote({tone:'crit',text:'Delete failed. Please try again.'})
            })
      }

    /* Every colour below comes from theme/tokens.css via getComputedStyle,
       so the table repaints with the rest of the product on a theme change. */
    const tableStyles=useMemo(()=>buildTableStyles(tokens),[tokens])

    const shown=useMemo(()=>{
      const needle=q.trim().toLowerCase()
      if (!needle) return data
      return data.filter(item=>{
        const name=String(item.SportName||'').toLowerCase()
        const tags=String(item.Tags||'').toLowerCase()
        return name.indexOf(needle)>-1 || tags.indexOf(needle)>-1
      })
    },[data,q])

    const total=data.length

    return (
        <div className="st-page">

          {note
            ? <div className="st-note" data-tone={note.tone} role="alert">
                <span className="st-note-text">{note.text}</span>
                <button type="button" className="st-note-x" aria-label="Close" onClick={()=>setNote(null)}>
                  <IconX/>
                </button>
              </div>
            : null}

          <Panel
            title="Sports"
            subtitle={
              isLoading
                ? 'Loading…'
                : total===0
                  ? 'No records'
                  : total+(total===1 ? ' sport' : ' sports')
            }
            actions={
              <>
                {total>0
                  ? <span className="st-search">
                      <IconSearch/>
                      <input
                        className="st-input"
                        type="search"
                        value={q}
                        placeholder="Search"
                        aria-label="Search sports"
                        onChange={(e)=>setQ(e.target.value)}
                      />
                    </span>
                  : null}
                <Btn variant="accent" onClick={()=>showModal(0)}>+ New sport</Btn>
              </>
            }
          >
            {isLoading
              ? <div className="st-sk" aria-hidden="true">
                  <span className="skeleton"></span>
                  <span className="skeleton"></span>
                  <span className="skeleton"></span>
                  <span className="skeleton"></span>
                </div>
              : failed
                ? <div className="st-empty">
                    <div className="st-empty-t">Sports could not be loaded</div>
                    <div className="st-empty-s">
                      The server did not respond. Nothing has been lost — please try again.
                    </div>
                    <div className="st-empty-a">
                      <Btn variant="accent" onClick={loadData}>Try again</Btn>
                    </div>
                  </div>
                : total===0
                  ? <div className="st-empty">
                      <div className="st-empty-t">No sports yet</div>
                      <div className="st-empty-s">
                        A sport sets the stat categories users see on their profile.
                      </div>
                      <div className="st-empty-a">
                        <Btn variant="accent" onClick={()=>showModal(0)}>+ New sport</Btn>
                      </div>
                    </div>
                  : shown.length===0
                    ? <div className="st-empty">
                        <div className="st-empty-t">No matches for “{q.trim()}”</div>
                        <div className="st-empty-s">
                          There are {total} {total===1 ? 'sport' : 'sports'}. Clear the filter to see them all.
                        </div>
                        <div className="st-empty-a">
                          <Btn onClick={()=>setQ('')}>Clear filter</Btn>
                        </div>
                      </div>
                    : <div className="st-table">
                        <DataTable
                          columns={columns}
                          data={shown}
                          customStyles={tableStyles}
                          highlightOnHover
                          persistTableHead
                          noDataComponent={<div className="st-empty-s">No records.</div>}
                        />
                      </div>}
          </Panel>

          {show
            ? <div className="st-modal" role="dialog" aria-modal="true" aria-labelledby="st-sport-title">
                <div className="st-modal-panel">

                  <div className="st-modal-head">
                    <div className="st-modal-headline">
                      <div className="st-modal-title" id="st-sport-title">{record.ModalTitle}</div>
                      <div className="st-modal-sub">
                        {record.id>0 ? 'Edit sport' : 'New sport'}
                      </div>
                    </div>
                    <button type="button" className="st-modal-x" aria-label="Close" onClick={handleClose}>
                      <IconX/>
                    </button>
                  </div>

                  <div className="st-modal-body">

                    {modalNote
                      ? <div className="st-note" data-tone={modalNote.tone} role="alert">
                          <span className="st-note-text">{modalNote.text}</span>
                        </div>
                      : null}

                    <form className="st-form" onSubmit={save}>

                      <div className="st-field">
                        <label className="mono st-label" htmlFor="st-sport-name">Title</label>
                        <input
                          id="st-sport-name"
                          className="st-input"
                          type="text"
                          required
                          value={record.Title===undefined || record.Title===null ? '' : record.Title}
                          onChange={(e)=>setRecord({...record,Title:e.target.value})}
                        />
                      </div>

                      <div className="st-field">
                        <label className="mono st-label" htmlFor="st-sport-tags">Stat Categories (comma separated)</label>
                        <input
                          id="st-sport-tags"
                          className="st-input"
                          type="text"
                          required
                          value={record.Tags===undefined || record.Tags===null ? '' : record.Tags}
                          onChange={(e)=>setRecord({...record,Tags:e.target.value})}
                        />
                        <div className="st-hint">
                          Separate categories with commas, e.g. Points, Assists, Rebounds.
                        </div>
                      </div>

                      {askDelete
                        ? <div className="st-confirm" role="alert">
                            <span className="st-confirm-t">
                              This will permanently delete the sport <b>{record.Title}</b>.
                            </span>
                            <span className="st-confirm-a">
                              <Btn onClick={()=>setAskDelete(false)}>Cancel</Btn>
                              <Btn className="st-crit" onClick={deleteRecord}>Delete permanently</Btn>
                            </span>
                          </div>
                        : null}

                      <div className="st-foot">
                        {record.id>0 && !askDelete
                          ? <span className="st-foot-far">
                              <Btn className="st-crit" onClick={()=>setAskDelete(true)}>Delete</Btn>
                            </span>
                          : null}
                        <Btn onClick={handleClose}>Cancel</Btn>
                        <Btn variant="accent" type="submit">Save</Btn>
                      </div>

                    </form>

                    {record.id>0
                      ? <div className="st-modal-sep">
                          <FileUploadSports  id={record.id} type="3" />
                        </div>
                      : null}

                  </div>
                </div>
              </div>
            : null}

        </div>
    )
}
